import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Skeleton,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ShowChart,
  Sync,
  Storage,
  Refresh,
  CloudSync,
  Update,
  Info,
} from '@mui/icons-material';
import axios from 'axios';
import MetricCard from '../components/shared/MetricCard';
import MobileStockCard from '../components/shared/MobileStockCard';
import { formatCurrency, formatPercent, getPnLColor } from '../utils/formatters';

const Portfolio = () => {
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [updatingPrices, setUpdatingPrices] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [groupBy, setGroupBy] = useState('sector');
  const [sortField, setSortField] = useState('symbol');
  const [sortDirection, setSortDirection] = useState('asc');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async (forceSync = false, updatePrices = false) => {
    try {
      setLoading(true);
      setError('');
      
      let url = '/portfolio';
      const params = [];
      if (forceSync) params.push('sync=true');
      if (updatePrices) params.push('updatePrices=true');
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url);
      
      setPortfolioData(response.data);
      setLastSyncTime(new Date());
      
      if (forceSync) {
        showSnackbar('Portfolio synced successfully', 'success');
      } else if (updatePrices && response.data.pricesUpdated) {
        showSnackbar('Portfolio prices updated', 'success');
      } else if (updatePrices && !response.data.pricesUpdated) {
        showSnackbar('Prices are already up to date', 'info');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch portfolio data';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
      console.error('Portfolio fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const syncWithAlpaca = async () => {
    try {
      setSyncing(true);
      setError('');
      
      const response = await axios.post('/portfolio/sync');
      
      if (response.data.synced) {
        setPortfolioData(response.data.portfolio);
        setLastSyncTime(new Date());
        showSnackbar(`Successfully synced ${response.data.positionsCount} positions`, 'success');
      } else {
        showSnackbar(response.data.message, 'warning');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to sync with Alpaca';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
      console.error('Sync error:', err);
    } finally {
      setSyncing(false);
    }
  };

  const updatePrices = async () => {
    try {
      setUpdatingPrices(true);
      setError('');
      
      // Use the new updatePrices parameter instead of separate endpoint
      await fetchPortfolio(false, true);
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update prices';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
      console.error('Price update error:', err);
    } finally {
      setUpdatingPrices(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStockClick = (symbol) => {
    navigate(`/stock/${symbol}`);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (item, field) => {
    switch (field) {
      case 'symbol': return item.symbol || '';
      case 'name': return item.name || item.company_name || '';
      case 'shares': return parseFloat(item.shares) || 0;
      case 'currentPrice': return parseFloat(item.currentPrice) || 0;
      case 'currentValue': return parseFloat(item.currentValue) || 0;
      case 'gainLoss': return parseFloat(item.gainLoss) || 0;
      case 'gainLossPercent': return parseFloat(item.gainLossPercent) || 0;
      default: return '';
    }
  };

  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  };

  const getGrouped = (portfolioItems) => {
    if (groupBy === 'none') return { 'All Holdings': sortItems(portfolioItems) };
    
    const keyFn = groupBy === 'sector'
      ? (item) => item.sector || 'Other'
      : (item) => parseFloat(item.gainLoss) >= 0 ? 'Profitable' : 'Losing';

    const grouped = portfolioItems.reduce((acc, item) => {
      const group = keyFn(item);
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {});

    Object.keys(grouped).forEach(g => { grouped[g] = sortItems(grouped[g]); });
    return grouped;
  };

  const getSortedGroupKeys = (grouped) => {
    return Object.keys(grouped).sort((a, b) => {
      if (a === 'Other' || a === 'All Holdings') return 1;
      if (b === 'Other' || b === 'All Holdings') return -1;
      return a.localeCompare(b);
    });
  };

  if (loading) {
    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rounded" height={90} sx={{ borderRadius: 2.5 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error && !portfolioData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={() => fetchPortfolio()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  const { portfolio, summary } = portfolioData || { 
    portfolio: [], 
    summary: {}
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" component="h1">
          Portfolio Overview
        </Typography>
        <Tooltip title="Refresh portfolio data">
          <IconButton onClick={() => fetchPortfolio()} disabled={loading} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary MetricCards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label="Available Balance" value={formatCurrency(summary.balance || 0)} icon={<AccountBalance />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Total Value" value={formatCurrency(summary.totalValue || 0)} icon={<ShowChart />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Total Invested" value={formatCurrency(summary.totalInvested || 0)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            label="Total Gain/Loss"
            value={formatCurrency(summary.totalGainLoss || 0)}
            color={getPnLColor(summary.totalGainLoss || 0)}
            trend={summary.totalGainLossPercent ? { value: summary.totalGainLossPercent, label: formatPercent(summary.totalGainLossPercent) } : undefined}
          />
        </Grid>
      </Grid>

      {/* Holdings */}
      <Grid container spacing={3}>
        {/* Holdings Table — Full width (no donut chart) */}
        <Grid item xs={12}>
      {/* Holdings header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">
          Holdings ({portfolio.length} positions)
        </Typography>
        {portfolio.length > 0 && (
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="textSecondary">Group by:</Typography>
            <ToggleButtonGroup
              value={groupBy}
              exclusive
              onChange={(e, val) => { if (val) setGroupBy(val); }}
              size="small"
            >
              <ToggleButton value="sector">Sector</ToggleButton>
              <ToggleButton value="gainloss">Gain/Loss</ToggleButton>
              <ToggleButton value="none">None</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
      </Box>

      {portfolio.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No holdings yet. Start by searching and buying some stocks!
            </Typography>
          </CardContent>
        </Card>
      ) : (
        (() => {
          const grouped = getGrouped(portfolio);
          const sortedKeys = getSortedGroupKeys(grouped);
          const totalPortfolioValue = portfolio.reduce((s, st) => s + (parseFloat(st.currentValue) || 0), 0);

          const sortableHeader = (field, label, align = 'left') => (
            <TableCell align={align} sortDirection={sortField === field ? sortDirection : false}>
              <TableSortLabel
                active={sortField === field}
                direction={sortField === field ? sortDirection : 'asc'}
                onClick={() => handleSort(field)}
              >
                <strong>{label}</strong>
              </TableSortLabel>
            </TableCell>
          );

          return sortedKeys.map((group) => {
            const groupValue = grouped[group].reduce((s, st) => s + (parseFloat(st.currentValue) || 0), 0);
            const groupPct = totalPortfolioValue > 0 ? (groupValue / totalPortfolioValue) * 100 : 100;

            return (
            <Box key={group} sx={{ mb: 4 }}>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5, mb: 0,
                  backgroundColor: 'action.selected',
                  borderRadius: isMobile ? 2 : '4px 4px 0 0',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  border: 1,
                  borderColor: 'divider',
                  borderBottom: isMobile ? 1 : 0,
                  borderBottomColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{group}</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip
                    label={`${grouped[group].length} position${grouped[group].length > 1 ? 's' : ''}`}
                    size="small"
                  />
                  <Chip
                    label={`${formatCurrency(groupValue)} (${groupPct.toFixed(0)}%)`}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Box>
              </Paper>

              {/* Mobile: Card layout */}
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  {grouped[group].map((stock) => (
                    <MobileStockCard
                      key={stock.symbol}
                      symbol={stock.symbol}
                      name={stock.name || stock.company_name || stock.symbol}
                      shares={stock.shares}
                      currentPrice={stock.currentPrice}
                      avgPrice={stock.average_price}
                      currentValue={stock.currentValue}
                      gainLoss={stock.gainLoss}
                      gainLossPercent={stock.gainLossPercent}
                      onClick={() => handleStockClick(stock.symbol)}
                    />
                  ))}
                </Box>
              ) : (
              /* Desktop: Table layout */
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: '0 0 4px 4px' }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                      {sortableHeader('symbol', 'Symbol')}
                      {sortableHeader('name', 'Company')}
                      {sortableHeader('shares', 'Shares', 'right')}
                      <TableCell align="right"><strong>Avg Price</strong></TableCell>
                      {sortableHeader('currentPrice', 'Current Price', 'right')}
                      {sortableHeader('currentValue', 'Current Value', 'right')}
                      {sortableHeader('gainLoss', 'Gain/Loss', 'right')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grouped[group].map((stock) => (
                      <TableRow
                        key={stock.symbol}
                        hover
                        onClick={() => handleStockClick(stock.symbol)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {stock.symbol}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {stock.name || stock.company_name || stock.symbol}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(stock.shares).toFixed(4)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(stock.average_price)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(stock.currentPrice)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(stock.currentValue)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            color={parseFloat(stock.gainLoss) >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(stock.gainLoss)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color={parseFloat(stock.gainLossPercent) >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatPercent(stock.gainLossPercent)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Group Total Row */}
                    {(() => {
                      const groupTotalValue = grouped[group].reduce((s, st) => s + (parseFloat(st.currentValue) || 0), 0);
                      const groupTotalInvested = grouped[group].reduce((s, st) => s + (parseFloat(st.totalInvested) || 0), 0);
                      const groupTotalGainLoss = groupTotalValue - groupTotalInvested;
                      const groupGainLossPct = groupTotalInvested > 0 ? (groupTotalGainLoss / groupTotalInvested) * 100 : 0;
                      return (
                        <TableRow sx={{ backgroundColor: 'action.hover', borderTop: 2, borderColor: 'divider' }}>
                          <TableCell colSpan={4} />
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">TOTAL</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(groupTotalValue)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight="bold" color={groupTotalGainLoss >= 0 ? 'success.main' : 'error.main'}>
                              {formatCurrency(groupTotalGainLoss)}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color={groupGainLossPct >= 0 ? 'success.main' : 'error.main'}>
                              {formatPercent(groupGainLossPct)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Box>
          );
          });
        })()
      )}

      {/* Snackbar for notifications */}
      </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Portfolio;
